import { Navigate, useLocation } from 'react-router-dom';

/**
 * Sends a retired report path to the workspace, keeping the user's scope.
 *
 * A bookmark or an emailed link to `/reports/attendance?from=…&to=…` is someone
 * mid-task, not someone browsing — dropping them on an unfiltered workspace
 * would silently change the answer they were looking at. So the existing query
 * string is carried over verbatim and `lens` is layered on top, which lands them
 * on the same data in the frame that replaced their old page.
 *
 * `replace` keeps the dead path out of history: pressing Back should return to
 * wherever they came from, not bounce through the redirect again.
 *
 * The target is `/reports/workspace`. The workspace is the front door again,
 * so this is where the job board briefly lived and where every generation of
 * saved link now resolves: `/reports/attendance`, `/reports/workspace` and
 * the `/reports?lens=…` URLs all land on the same filtered numbers. The
 * workspace itself still reads
 * `?lens=` and maps each retired lens onto the row set it used to show, so a
 * link keeps its ANSWER and not merely its address.
 */
export const WORKSPACE_PATH = '/reports/workspace';

export function RedirectToWorkspace({ lens }: { lens?: string }): JSX.Element {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  // 'attendance' is the workspace default and is represented by the absence of
  // the param, matching how the workspace writes the URL.
  if (lens && lens !== 'attendance') params.set('lens', lens);
  const qs = params.toString();
  return <Navigate to={{ pathname: WORKSPACE_PATH, search: qs ? `?${qs}` : '' }} replace />;
}

export default RedirectToWorkspace;
